import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { Resource } from '../../common/model/type/resource.enum';
import { FilterModel } from '../../common/model/ui/filter.model';
import { OrderBy } from '../../common/types';
import { isFilterApplied } from '../../common/utils/services/list-filter-utils';
import {
  getListRange,
  getOrderListFilters,
  getOrderListSortOptions,
} from '../../common/utils/services/order-list-utils';
import { OrderFields } from '../../firebase/document-field.enums';
import useAuthorized from '../../hooks/use-authorized';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import OrdersService from '../../services/orders.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvOrderList from '../UI/organisms/OvOrderList';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

export const ADMIN_ORDERS_LIST_LIMIT = 10;

const AdminOrdersList = () => {
  const { t } = useTranslation();

  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const [internalId, setInternalId] = useQueryParam(
    'internal_id',
    withDefault(StringParam, '')
  );

  const [userEmail, setUserEmail] = useQueryParam(
    'user_email',
    withDefault(StringParam, '')
  );

  const [status, setStatus] = useQueryParam(
    'status',
    withDefault(StringParam, '')
  );

  const [paymentStatus, setPaymentStatus] = useQueryParam(
    'payment_status',
    withDefault(StringParam, '')
  );

  const [fulfillmentStatus, setFulfillmentStatus] = useQueryParam(
    'fulfillment_status',
    withDefault(StringParam, '')
  );

  const applicationOperations = useAuthorized(Resource.Application);
  const orderOperations = useAuthorized(Resource.Order);

  const orderCreateAllowed =
    orderOperations.create || applicationOperations.supervise;

  const query = queryString.stringify({
    limit: ADMIN_ORDERS_LIST_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
    internal_id: internalId,
    user_email: userEmail,
    status,
    payment_status: paymentStatus,
    fulfillment_status: fulfillmentStatus,
  });

  const {
    data: paginatedOrders,
    isPending: isGetOrdersPending,
    isFetching: isGetOrdersFetching,
    isError: isGetOrdersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ['admin-orders', query],
    queryFn: () => OrdersService.loadOrdersByQuery(query),
    retry: 3,
    enabled: !!orderBy && !!orderDir,
    placeholderData: keepPreviousData,
  });

  const orders = paginatedOrders?.docs ?? [];
  const total = paginatedOrders?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('created_at');
      setOrderDir('desc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const filters = getOrderListFilters({
    internalId,
    userEmail,
    status,
    paymentStatus,
    fulfillmentStatus,
  });
  const hasAppliedFilters = filters.some(isFilterApplied);

  const goToPage = (_: React.ChangeEvent<unknown>, value: number) => {
    setOffset((value - 1) * ADMIN_ORDERS_LIST_LIMIT);
  };

  const onSort = ({ order_by, order_dir }: OrderBy) => {
    setOffset(0);
    setOrderBy(order_by);
    setOrderDir(order_dir);
  };

  const onFilterChange = (filter: FilterModel) => {
    const value = filter.value || null;

    switch (filter.fieldName) {
      case OrderFields.internal_id:
        setOffset(0);
        setInternalId(value);
        break;

      case OrderFields.user_email:
        setOffset(0);
        setUserEmail(value);
        break;

      case OrderFields.status:
        setOffset(0);
        setStatus(value);
        break;

      case OrderFields.payment_status:
        setOffset(0);
        setPaymentStatus(value);
        break;

      case OrderFields.fulfillment_status:
        setOffset(0);
        setFulfillmentStatus(value);
        break;

      default:
        break;
    }
  };

  const onFilterClear = () => {
    setOffset(0);
    setInternalId(null);
    setUserEmail(null);
    setStatus(null);
    setPaymentStatus(null);
    setFulfillmentStatus(null);
  };

  const isLoading = isGetOrdersFetching || isGetOrdersPending;
  const range = getListRange(offset ?? 0, ADMIN_ORDERS_LIST_LIMIT, total);

  const getResultSummary = () => {
    if (isGetOrdersError) {
      return t('common.listResults.unavailable');
    }

    if (isGetOrdersPending) {
      return t('common.listResults.loading');
    }

    return t('orders.resultRange', { ...range, total });
  };

  const renderContent = () => {
    if (isGetOrdersError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('orders.states.errorTitle')}
          description={t('orders.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetchOrders()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isGetOrdersPending) {
      return null;
    }

    if (orders.length > 0) {
      return (
        <OvOrderList
          orders={orders}
          orderBy={orderBy}
          orderDir={orderDir}
          onSort={onSort}
        />
      );
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('orders.states.noResultsTitle')}
          description={t('orders.states.noResultsDescription')}
        >
          <OvCompactButton onClick={onFilterClear}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<ShoppingCartIcon />}
        title={t('orders.states.emptyTitle')}
        description={
          orderCreateAllowed
            ? t('orders.states.emptyDescription')
            : t('orders.states.emptyReadOnlyDescription')
        }
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('app.subPages.menu.orders')}
      icon={<ShoppingCartIcon />}
      headerAction={
        orderCreateAllowed && (
          <NavLink to="/orders/create">
            <OvCompactButton icon={<AddCircleIcon />}>
              {t('lots.createNew')}
            </OvCompactButton>
          </NavLink>
        )
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={onFilterChange}
          onClearFilters={onFilterClear}
        >
          <OvListSortSelect
            options={getOrderListSortOptions(t)}
            orderBy={orderBy}
            orderDir={orderDir}
            onChange={onSort}
          />
        </OvListFilterBar>
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={ADMIN_ORDERS_LIST_LIMIT}
      offset={offset}
      onPageChange={goToPage}
      isBusy={isLoading}
    >
      {renderContent()}
    </OvRecordListPage>
  );
};

export default AdminOrdersList;
